<template>
  <div>
    <v-card elevation="0">
      <!-- Time Doctor Integration (Admin Only) -->
      <div v-if="isAdmin">
          <v-card-title>
            <v-icon left color="primary">mdi-clock-check-outline</v-icon>
            <span style="font-size: 1.3rem; font-weight: 600">
              Time Doctor Integration
            </span>
          </v-card-title>
          <v-card-subtitle>
            Manage Time Doctor API connection for payroll hour tracking
          </v-card-subtitle>
          <v-divider></v-divider>
          <v-card-text>
            <!-- Loading State -->
            <div v-if="isCheckingTimeDoctorAuth" class="text-center py-6">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-4">Checking Time Doctor connection status...</div>
            </div>

            <!-- Connected State -->
            <div v-else-if="timeDoctorAuthorized">
              <v-alert type="success" dense outlined class="mb-4">
                <v-row no-gutters align="center">
                  <v-col>
                    <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                    <strong>Time Doctor Connected</strong>
                    <div class="text-caption mt-1">
                      The Time Doctor API is connected and active. All employees can fetch their tracked hours.
                    </div>
                  </v-col>
                </v-row>
              </v-alert>

              <v-card outlined class="pa-4">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-subtitle-1 font-weight-medium">Connection Status</div>
                    <div class="text-caption grey--text">API is authenticated and ready to use</div>
                  </div>
                  <v-chip color="success" small>
                    <v-icon left small>mdi-wifi</v-icon>
                    Connected
                  </v-chip>
                </div>
                <v-divider class="my-4"></v-divider>
                <div class="text-right">
                  <v-btn color="error" outlined @click="disconnectTimeDoctor" :loading="isDisconnecting" class="text-none">
                    <v-icon left small>mdi-link-off</v-icon>
                    Disconnect Time Doctor
                  </v-btn>
                </div>
              </v-card>
            </div>

            <!-- Not Connected State -->
            <div v-else class="text-center py-6">
              <v-icon size="80" color="grey lighten-1">mdi-clock-alert-outline</v-icon>
              <div class="mt-4 text-h6">Time Doctor Not Connected</div>
              <div class="mt-2 text-body-2 grey--text mb-6" style="max-width: 500px; margin: 0 auto;">
                Connect the Time Doctor API to enable automatic hour tracking for all employees.
                Once connected, employees will be able to view their tracked hours in the payroll submission form.
              </div>
              <v-btn color="primary" large @click="authorizeTimeDoctor" :loading="isCheckingTimeDoctorAuth" class="text-none">
                <v-icon left>mdi-link-variant</v-icon>
                Connect Time Doctor
              </v-btn>
              
              <v-alert type="info" dense text class="mt-6" style="max-width: 600px; margin: 0 auto;">
                <v-icon small color="info" class="mr-1">mdi-information</v-icon>
                <span class="text-caption">
                  You will be redirected to Time Doctor to authorize the connection. 
                  After authorization, you'll be returned to this page.
                </span>
              </v-alert>
            </div>
          </v-card-text>
      </div>
      <div v-else class="pa-8 text-center">
        <v-icon size="80" color="grey lighten-1">mdi-lock</v-icon>
        <div class="mt-4 text-h6">Admin Access Required</div>
        <div class="mt-2 text-body-2 grey--text">
          Only administrators can access Time Doctor settings.
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "AdminPayrollView",
  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
    validationRules: {
      type: Array,
      default: () => [],
    },
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeTab: 0,
      currencies: ["USD", "PHP", "EUR", "GBP"],
      localSettings: {},
      localRules: [],
      nextRuleId: 100,
      // Time Doctor
      timeDoctorAuthorized: false,
      timeDoctorAuthUrl: null,
      isCheckingTimeDoctorAuth: false,
      isDisconnecting: false,
    };
  },
  computed: {
    isAdmin() {
      // daniza@stagit.ie manages Time Doctor without being a system admin
      return this.user?.role_id === 1 || Number(this.user?.id) === 122 || this.user?.email?.toLowerCase() === 'daniza@stagit.ie';
    },
  },
  watch: {
    settings: {
      immediate: true,
      handler(newSettings) {
        this.localSettings = { ...newSettings };
      },
    },
    validationRules: {
      immediate: true,
      handler(newRules) {
        this.localRules = JSON.parse(JSON.stringify(newRules));
      },
    },
  },
  mounted() {
    // Check Time Doctor auth status if admin
    if (this.isAdmin) {
      this.checkTimeDoctorAuth();
      // Check for OAuth callback code in URL
      this.handleOAuthCallback();
    }
  },
  methods: {
    // Time Doctor methods
    async checkTimeDoctorAuth() {
      this.isCheckingTimeDoctorAuth = true;
      try {
        const response = await this.$axios.get('/api/payroll/time-doctor/auth-status');
        console.log('Time Doctor auth status:', response.data);
        
        if (response.data.response && response.data.data) {
          this.timeDoctorAuthorized = response.data.data.is_authorized;
          this.timeDoctorAuthUrl = response.data.data.auth_url;
          
          // Log the authenticated Time Doctor user
          if (response.data.data.authenticated_user) {
            console.log('Time Doctor Authenticated User/Companies:', response.data.data.authenticated_user);
            console.log('  - Companies Count:', response.data.data.authenticated_user.companies_count);
            if (response.data.data.authenticated_user.company_name) {
              console.log('  - Primary Company:', response.data.data.authenticated_user.company_name, 
                         '(ID:', response.data.data.authenticated_user.company_id + ')');
            }
          } else {
            console.log('Time Doctor: Not authorized or no user info available');
          }
        }
      } catch (error) {
        console.error('Error checking Time Doctor auth:', error);
        this.timeDoctorAuthorized = false;
      } finally {
        this.isCheckingTimeDoctorAuth = false;
      }
    },
    
    authorizeTimeDoctor() {
      if (this.timeDoctorAuthUrl) {
        console.log('Redirecting to Time Doctor auth:', this.timeDoctorAuthUrl);
        window.location.href = this.timeDoctorAuthUrl;
      } else {
        this.$toast.error('Unable to get authorization URL');
      }
    },
    
    /**
     * Handle OAuth callback - check URL for auth code (admin redirected here after auth)
     */
    async handleOAuthCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        console.log('OAuth callback detected, exchanging code for token...');
        this.isCheckingTimeDoctorAuth = true;
        
        try {
          const response = await this.$axios.get('/api/payroll/time-doctor/callback', {
            params: { code }
          });
          
          console.log('OAuth callback response:', response.data);
          
          if (response.data && response.data.response) {
            this.$toast.success('Time Doctor connected successfully!');
            this.timeDoctorAuthorized = true;
            
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Refresh auth status
            await this.checkTimeDoctorAuth();
          } else {
            this.$toast.error(response.data?.message || 'Failed to connect Time Doctor');
          }
        } catch (error) {
          console.error('OAuth callback error:', error);
          this.$toast.error(error.response?.data?.message || 'Failed to connect Time Doctor');
        } finally {
          this.isCheckingTimeDoctorAuth = false;
        }
      }
    },
    
    async disconnectTimeDoctor() {
      this.isDisconnecting = true;
      try {
        const response = await this.$axios.post('/api/payroll/time-doctor/disconnect');
        
        if (response.data.response) {
          this.$toast.success('Time Doctor disconnected');
          this.timeDoctorAuthorized = false;
          
          // Refresh auth status to get new auth URL
          await this.checkTimeDoctorAuth();
        }
      } catch (error) {
        console.error('Error disconnecting Time Doctor:', error);
        this.$toast.error('Failed to disconnect Time Doctor');
      } finally {
        this.isDisconnecting = false;
      }
    },
    
    // Settings
    resetSettings() {
      this.localSettings = { ...this.settings };
      this.$toast.info("Settings reset to last saved values");
    },
    saveSettings() {
      this.$emit("updateSettings", { ...this.localSettings });
    },
    // Rules
    resetRules() {
      this.localRules = JSON.parse(JSON.stringify(this.validationRules));
      this.$toast.info("Rules reset to last saved values");
    },
    saveRules() {
      this.$emit("updateRules", [...this.localRules]);
    },
    addNewRule() {
      this.localRules.push({
        id: this.nextRuleId++,
        name: "New Rule",
        description: "Enter rule description",
        value: 0,
        unit: "percent",
        enabled: false,
      });
    },
    removeRule(index) {
      this.localRules.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.v-expansion-panel-header {
  min-height: 64px !important;
}

.v-expansion-panel--active>.v-expansion-panel-header {
  min-height: 64px !important;
}
</style>
